/**
 * @fileoverview Add POD Claim Panel — slide-over form for lodging a new
 * Proof of Debt claim.
 *
 * Purpose: Creates a `podClaims` row, either linked to an existing
 * creditor on the creditors table (name / secured status seeded from the
 * liability entry) or free-standing for creditors not yet on the table.
 */

import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { convexQuery, useConvexMutation } from "@convex-dev/react-query";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { toast } from "sonner";
import { extractErrorMessage } from "@/utils/errors";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/ui/composed/overlays/sheet";
import { Button } from "@/ui/primitives/button";
import { Input } from "@/ui/primitives/input";
import { Textarea } from "@/ui/primitives/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui/primitives/select";
import { ScrollArea } from "@/ui/primitives/scroll-area";
import { DatePicker } from "@/ui/composed/inputs/date-picker";
import {
  POD_CREDITOR_TYPE_LABELS,
  type PodCreditorType,
  type PodStatusRow,
} from "./podShared";

const MANUAL_CREDITOR = "__manual__";

interface CreditorOption {
  id: string;
  name: string;
  secured: boolean;
}

export function AddPodClaimPanel({
  jobId,
  open,
  onClose,
  onCreated,
}: {
  jobId: Id<"jobs">;
  open: boolean;
  onClose: () => void;
  onCreated: (id: Id<"podClaims">) => void;
}) {
  const [liabilityEntryId, setLiabilityEntryId] =
    useState<string>(MANUAL_CREDITOR);
  const [creditorName, setCreditorName] = useState("");
  const [creditorType, setCreditorType] =
    useState<PodCreditorType>("unsecured");
  const [claimedTotal, setClaimedTotal] = useState("");
  const [podDate, setPodDate] = useState("");
  const [notes, setNotes] = useState("");

  // Creditors without an existing POD claim, offered for linking.
  const { data: rawEntries } = useQuery(
    convexQuery(
      api.jobs.summarization.publicQueries.listLiabilityEntriesForJob,
      { jobId },
    ),
  );
  const { data: rawStatuses } = useQuery(
    convexQuery(
      api.jobs.recoveries.publicQueries.listPodStatusForLiabilityEntries,
      { jobId },
    ),
  );

  const creditorOptions = useMemo<CreditorOption[]>(() => {
    const entries = (rawEntries ?? []) as Array<{
      _id: string;
      creditorName: string;
      secured: boolean;
      status: string;
      isExcluded?: boolean;
    }>;
    // Only a hard link takes a creditor out of the picker. A name match is
    // reported on the creditors table but is too loose to block the
    // practitioner from lodging a claim against the row themselves.
    const claimed = new Set(
      ((rawStatuses ?? []) as PodStatusRow[])
        .filter((s) => !s.matchedByName)
        .map((s) => s.liabilityEntryId),
    );
    return entries
      .filter(
        (e) => e.status === "active" && !e.isExcluded && !claimed.has(e._id),
      )
      .map((e) => ({ id: e._id, name: e.creditorName, secured: e.secured }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [rawEntries, rawStatuses]);

  const createClaim = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.recoveries.publicMutations.createPodClaim,
    ),
  });

  function resetForm() {
    setLiabilityEntryId(MANUAL_CREDITOR);
    setCreditorName("");
    setCreditorType("unsecured");
    setClaimedTotal("");
    setPodDate("");
    setNotes("");
  }

  useEffect(() => {
    if (open) resetForm();
  }, [open]);

  const isLinked = liabilityEntryId !== MANUAL_CREDITOR;
  const selectedCreditor = creditorOptions.find(
    (c) => c.id === liabilityEntryId,
  );
  const canSubmit = isLinked || creditorName.trim().length > 0;

  // Seed the secured/unsecured type from the linked creditor row.
  function handleCreditorChange(value: string) {
    setLiabilityEntryId(value);
    const option = creditorOptions.find((c) => c.id === value);
    if (option) {
      setCreditorType(option.secured ? "secured" : "unsecured");
    }
  }

  async function handleSubmit() {
    if (!canSubmit || createClaim.isPending) return;
    const parsedTotal = claimedTotal ? parseFloat(claimedTotal) : undefined;

    try {
      const id = (await createClaim.mutateAsync({
        jobId,
        liabilityEntryId: isLinked
          ? (liabilityEntryId as Id<"liabilityEntries">)
          : undefined,
        creditorName: isLinked ? undefined : creditorName.trim(),
        creditorType,
        claimedTotal:
          parsedTotal !== undefined && Number.isFinite(parsedTotal)
            ? parsedTotal
            : undefined,
        podDate: podDate || undefined,
        notes: notes.trim() || undefined,
      })) as Id<"podClaims">;
      resetForm();
      onCreated(id);
    } catch (error) {
      toast.error(extractErrorMessage(error, "Failed to create POD claim"));
    }
  }

  return (
    <Sheet
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) {
          resetForm();
          onClose();
        }
      }}
    >
      <SheetContent className="flex w-full flex-col overflow-hidden p-0 sm:max-w-md">
        <div className="px-6 pb-4 pt-6">
          <SheetHeader>
            <SheetTitle className="text-lg font-bold">New POD Claim</SheetTitle>
            <SheetDescription>
              Lodge a Proof of Debt claim for a creditor. Link it to the
              creditors table so POD status shows there too.
            </SheetDescription>
          </SheetHeader>
        </div>

        <ScrollArea className="min-h-0 flex-1">
          <div className="space-y-4 px-6 pb-6">
            {/* Creditor */}
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Creditor *
              </label>
              <Select
                value={liabilityEntryId}
                onValueChange={handleCreditorChange}
              >
                <SelectTrigger className="text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={MANUAL_CREDITOR}>
                    Enter manually…
                  </SelectItem>
                  {creditorOptions.map((option) => (
                    <SelectItem key={option.id} value={option.id}>
                      {option.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {isLinked && selectedCreditor && (
                <p className="mt-1 text-xs text-muted-foreground">
                  Linked to the creditors table entry for{" "}
                  {selectedCreditor.name}.
                </p>
              )}
            </div>

            {/* Manual creditor name */}
            {!isLinked && (
              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Creditor Name *
                </label>
                <Input
                  value={creditorName}
                  onChange={(e) => setCreditorName(e.target.value)}
                  placeholder="e.g. Workers Compensation Nominal Insurer"
                />
              </div>
            )}

            {/* Creditor type */}
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Creditor Type
              </label>
              <Select
                value={creditorType}
                onValueChange={(v) => setCreditorType(v as PodCreditorType)}
              >
                <SelectTrigger className="text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(
                    Object.entries(POD_CREDITOR_TYPE_LABELS) as Array<
                      [PodCreditorType, string]
                    >
                  ).map(([value, label]) => (
                    <SelectItem key={value} value={value}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Claimed amount + POD date */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Amount Claimed
                </label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={claimedTotal}
                  onChange={(e) => setClaimedTotal(e.target.value)}
                  placeholder="0.00"
                />
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  POD Date
                </label>
                <DatePicker value={podDate} onChange={setPodDate} />
              </div>
            </div>

            {/* Notes */}
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Notes
              </label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Context for this claim (optional)"
                rows={3}
              />
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-2 pt-2">
              <Button
                variant="outline"
                onClick={() => {
                  resetForm();
                  onClose();
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={!canSubmit || createClaim.isPending}
              >
                {createClaim.isPending ? "Creating…" : "Create Claim"}
              </Button>
            </div>
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}
